import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PaletteIcon, RotateCcwIcon } from 'lucide-react';
import { themePresets, useTheme } from '../contexts/ThemeContext';

/** Palette switcher: four house themes plus free-form accent colour pickers. */
export function ThemeMenu() {
  const {
    presetId,
    setPreset,
    accent,
    secondary,
    setAccent,
    setSecondary,
    resetAccents,
    isCustomised
  } = useTheme();
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (hostRef.current && !hostRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <div ref={hostRef} className="relative" data-id="element-179" data-mp="2deghtpdk44u7">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} aria-label="Theme and colours" className="flex items-center gap-1.5 rounded-full border border-ink-line px-2.5 py-1.5 text-xs text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-180" data-mp="2iafahp5x490f">
        <PaletteIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-181" data-mp="1ynhd4pfue0fw" />
        <span className="flex gap-0.5" aria-hidden="true" data-id="element-182" data-mp="23m2bskfg4c28">
          <span className="h-2.5 w-2.5 rounded-full" style={{
          background: accent
        }} data-id="element-183" data-mp="23khmpabwhlbr" />
          <span className="h-2.5 w-2.5 rounded-full" style={{
          background: secondary
        }} data-id="element-184" data-mp="28f3y3dhzfdvu" />
        </span>
      </button>

      <AnimatePresence data-id="element-185" data-mp="1fwq6hvn8oo57">
        {open && <motion.div role="dialog" aria-label="Theme picker" initial={{
        opacity: 0,
        scale: 0.96,
        y: 6
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute bottom-11 right-0 z-40 w-72 origin-bottom-right rounded-xl border border-ink-line bg-ink-panel/95 p-3 shadow-2xl shadow-black/60 backdrop-blur" data-id="element-186" data-mp="1fvf522h9nsh9">
            <p className="px-1 pb-2 text-[11px] text-muted" data-id="element-187" data-mp="209kov4xdw8xv">Room palette</p>
            <ul className="grid grid-cols-2 gap-2" data-id="element-188" data-mp="1vagh8iq3yj9n">
              {themePresets.map(preset => <li key={preset.id} data-id="element-189" data-mp="299pwng6hp9ck">
                  <button type="button" onClick={() => setPreset(preset.id)} aria-pressed={presetId === preset.id && !isCustomised} className={`w-full rounded-lg border p-2 text-left transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${presetId === preset.id ? 'border-gold/70' : 'border-ink-line hover:border-gold/40'}`} data-id="element-190" data-mp="29ar9nq72g5wx">
                    <span className="flex h-10 items-end gap-1 rounded p-1.5" style={{
                background: preset.base
              }} aria-hidden="true" data-id="element-191" data-mp="3s5t15o6wpjyz">
                      <span className="h-5 w-5 rounded-full" style={{
                  background: preset.accent
                }} data-id="element-192" data-mp="ar4bboe8iiae" />
                      <span className="h-4 w-4 rounded-full" style={{
                  background: preset.secondary
                }} data-id="element-193" data-mp="fr8rfdb8db03" />
                      <span className="ml-auto h-3 w-3 rounded-sm" style={{
                  background: preset.line
                }} data-id="element-194" data-mp="12ckxkoto61l" />
                    </span>
                    <span className="mt-1.5 flex items-center gap-1 text-xs text-cream" data-id="element-195" data-mp="5zctq3d23mwe">
                      {preset.name}
                      {presetId === preset.id && !isCustomised && <CheckIcon className="h-3 w-3 text-gold" aria-hidden="true" data-id="element-196" data-mp="yi07expq3zng" />}
                    </span>
                    <span className="block text-[10px] italic text-muted" data-id="element-197" data-mp="35ygryb7o0lzw">{preset.blurb}</span>
                  </button>
                </li>)}
            </ul>

            <div className="mt-3 border-t border-ink-line pt-3" data-id="element-198" data-mp="2x0n4zqvsj4pe">
              <p className="px-1 pb-2 text-[11px] text-muted" data-id="element-199" data-mp="2s1tvsanjdzv1">Your own accents</p>
              <div className="flex items-center gap-3 px-1" data-id="element-200" data-mp="3poizqq1sumjj">
                <label className="flex flex-1 items-center gap-2 text-xs text-cream" data-id="element-201" data-mp="3pqvgx0zqgsvj">
                  <input type="color" value={accent} onChange={e => setAccent(e.target.value)} className="h-7 w-9 rounded" aria-label="Primary accent colour" data-id="element-202" data-mp="3kqhk6f3l15ct" />
                  Primary
                </label>
                <label className="flex flex-1 items-center gap-2 text-xs text-cream" data-id="element-203" data-mp="7j61x9zelp9q">
                  <input type="color" value={secondary} onChange={e => setSecondary(e.target.value)} className="h-7 w-9 rounded" aria-label="Secondary accent colour" data-id="element-204" data-mp="7h34w5zu2vvi" />
                  Glow
                </label>
              </div>
              {isCustomised && <button type="button" onClick={resetAccents} className="mt-3 flex items-center gap-1.5 rounded px-1 text-xs text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-205" data-mp="tswd4nogifw7">
                  <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-206" data-mp="1cht5un47h0cb" />
                  Back to theme defaults
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}